/**
 * StoreSuccessPage - Checkout success/completion page
 * Handles both Stripe and PayPal payment confirmations
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  CheckCircle,
  Download,
  Package,
  Loader2,
  XCircle,
  ArrowRight,
  Clock,
  RefreshCw,
  ShoppingBag,
  Music,
  FileDown,
} from 'lucide-react';
import { getBackendUrl } from '../config/api';

const API_URL = getBackendUrl();

const StoreSuccessPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, pending, error
  const [orderData, setOrderData] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const isPayPal = searchParams.get('paypal') === 'true';
  const paypalOrderId = searchParams.get('token'); // PayPal uses 'token' param

  // Poll for Stripe payment status
  const checkStripeStatus = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await fetch(`${API_URL}/api/store/checkout/status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid' || data.status === 'completed') {
        setStatus('success');
        setOrderData(data.order);
        setDownloads(data.downloads || []);
        toast.success('Payment successful!');
      } else if (data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired');
      } else {
        // Still pending, continue polling
        setStatus('pending');
        setPollCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  }, [sessionId]);

  // Capture PayPal payment
  const capturePayPalPayment = useCallback(async () => {
    if (!paypalOrderId) return;

    try {
      const response = await fetch(`${API_URL}/api/store/checkout/paypal/capture/${paypalOrderId}`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.payment_status === 'paid' || data.status === 'completed') {
        setStatus('success');
        setOrderData(data.order);
        setDownloads(data.downloads || []);
        toast.success('Payment successful!');
      } else {
        setStatus('error');
        toast.error('Payment was not completed');
      }
    } catch (error) {
      console.error('Error capturing PayPal payment:', error);
      setStatus('error');
    }
  }, [paypalOrderId]);

  // Initial load effect
  useEffect(() => {
    if (isPayPal && paypalOrderId) {
      capturePayPalPayment();
    } else if (sessionId) {
      checkStripeStatus();
    } else {
      setStatus('error');
    }
  }, [isPayPal, paypalOrderId, sessionId, capturePayPalPayment, checkStripeStatus]);

  // Polling effect for pending Stripe payments
  useEffect(() => {
    if (status === 'pending' && pollCount < 10 && !isPayPal) {
      const timer = setTimeout(() => {
        checkStripeStatus();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [status, pollCount, isPayPal, checkStripeStatus]);

  const handleDownload = async (downloadUrl) => {
    try {
      window.open(`${API_URL}${downloadUrl}`, '_blank');
      toast.success('Download started!');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl w-full"
      >
        {/* Loading State */}
        {status === 'loading' && (
          <div className="text-center py-12">
            <Loader2 className="w-16 h-16 text-violet-400 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Processing Payment...</h2>
            <p className="text-zinc-500">Please wait while we confirm your payment</p>
          </div>
        )}

        {/* Pending State */}
        {status === 'pending' && (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Payment Processing</h2>
            <p className="text-zinc-500 mb-4">
              Your payment is being processed. This may take a moment...
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-zinc-600">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Checking status... ({pollCount}/10)
            </div>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden">
            {/* Success header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.2 }}
              >
                <CheckCircle className="w-20 h-20 text-white mx-auto mb-4" />
              </motion.div>
              <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
              <p className="text-white/80">Thank you for your purchase</p>
            </div>

            {/* Order details */}
            <div className="p-6">
              {orderData && (
                <div className="mb-6">
                  <div className="flex items-center justify-between text-sm text-zinc-500 mb-4">
                    <span>Order ID</span>
                    <span className="font-mono">{orderData.id}</span>
                  </div>
                  
                  <div className="space-y-3">
                    {orderData.items?.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                        <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                          <Music className="w-5 h-5 text-violet-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-white">{item.product_name}</p>
                          <p className="text-xs text-zinc-500">Qty: {item.quantity}</p>
                        </div>
                        <p className="text-emerald-400 font-semibold">${item.item_total?.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                    <span className="text-lg font-medium text-zinc-400">Total Paid</span>
                    <span className="text-2xl font-bold text-white">${orderData.total?.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Downloads section */}
              {downloads.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Download className="w-5 h-5 text-emerald-400" />
                    Your Downloads
                  </h3>
                  <div className="space-y-2">
                    {downloads.map((download, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <FileDown className="w-5 h-5 text-zinc-500" />
                          <div>
                            <p className="font-medium text-white">{download.product_name}</p>
                            <p className="text-xs text-zinc-500">{download.file_size}</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleDownload(download.download_url)}
                          size="sm"
                          className="bg-emerald-500 hover:bg-emerald-600"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-600 mt-2 text-center">
                    Downloads are available for 5 uses each
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="space-y-3">
                <Link to="/store" className="block">
                  <Button className="w-full bg-violet-500 hover:bg-violet-600">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Continue Shopping
                  </Button>
                </Link>
                <Link to="/studio" className="block">
                  <Button variant="outline" className="w-full border-zinc-700">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Go to Studio
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="text-center py-12">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
            <p className="text-zinc-500 mb-6">
              Something went wrong with your payment. Please try again.
            </p>
            <div className="space-y-3">
              <Link to="/store">
                <Button className="bg-violet-500 hover:bg-violet-600">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Return to Store
                </Button>
              </Link>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default StoreSuccessPage;
